<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { IconImage } from '@/shared/ui';
  import MapIcon from '@/shared/assets/icons/MapIcon.svg';
  import ChartIcon from '@/shared/assets/icons/ChartIcon.svg';
  import EraserIcon from '@/shared/assets/icons/EraserIcon.svg';
  import ArrowRightCircleIcon from '@/shared/assets/icons/ArrowRightCircleIcon.svg';
  import { useFormBuilderToast } from '../../composables/useFormBuilderToast';
  import { useFormBuilderStore } from '../../model/form-builder.store';
  import { useFormRulesStore } from '../../model/form-rules.store';

  const ADD_ANCHOR_ACTION = 'add-anchor' as const;
  const CLEAR_ANCHOR_ACTION = 'clear-anchor' as const;
  const REPORT_FIELD_ACTION = 'mark-as-report-field' as const;
  const CLEAR_REPORT_FIELD_ACTION = 'clear-report-field' as const;
  const CLEAR_CONFIGURATIONS_ACTION = 'clear-configurations' as const;

  type ActionId =
    | 'add-rule'
    | typeof ADD_ANCHOR_ACTION
    | typeof CLEAR_ANCHOR_ACTION
    | typeof REPORT_FIELD_ACTION
    | typeof CLEAR_REPORT_FIELD_ACTION
    | typeof CLEAR_CONFIGURATIONS_ACTION;

  const props = withDefaults(
    defineProps<{
      showAddRule?: boolean;
    }>(),
    {
      showAddRule: false,
    }
  );

  const formBuilderStore = useFormBuilderStore();
  const formRulesStore = useFormRulesStore();
  const { fieldConfigurations, selectedFields } = storeToRefs(formRulesStore);
  const toast = useFormBuilderToast();

  const isSingleSelection = computed(
    () => formRulesStore.scopeType === 'single' && selectedFields.value.length === 1
  );
  const selectedField = computed(() => selectedFields.value[0]);

  function hasSelectedConfiguration(configuration: 'anchor' | 'report') {
    const configuredFieldIds = fieldConfigurations.value[configuration];
    return selectedFields.value.some((field) => configuredFieldIds.has(field.id));
  }

  function hasAllSelectedConfiguration(configuration: 'anchor' | 'report') {
    const configuredFieldIds = fieldConfigurations.value[configuration];
    return (
      selectedFields.value.length > 0 &&
      selectedFields.value.every((field) => configuredFieldIds.has(field.id))
    );
  }

  function isSingleActionHidden(actionId: ActionId, selectedId: string | undefined) {
    const anchorConfigured = selectedId
      ? fieldConfigurations.value.anchor.has(selectedId)
      : false;
    const reportConfigured = selectedId
      ? fieldConfigurations.value.report.has(selectedId)
      : false;

    const configuredActions: Partial<Record<ActionId, boolean>> = {
      [ADD_ANCHOR_ACTION]: anchorConfigured,
      [CLEAR_ANCHOR_ACTION]: !anchorConfigured,
      [REPORT_FIELD_ACTION]: reportConfigured,
      [CLEAR_REPORT_FIELD_ACTION]: !reportConfigured,
    };

    return configuredActions[actionId] ?? false;
  }

  function isBulkActionHidden(actionId: ActionId) {
    const configurationActions: Partial<Record<ActionId, boolean>> = {
      [CLEAR_ANCHOR_ACTION]: !hasSelectedConfiguration('anchor'),
      [CLEAR_REPORT_FIELD_ACTION]: !hasSelectedConfiguration('report'),
      [ADD_ANCHOR_ACTION]: hasAllSelectedConfiguration('anchor'),
      [REPORT_FIELD_ACTION]: hasAllSelectedConfiguration('report'),
    };

    return configurationActions[actionId] ?? false;
  }

  function isActionHidden(actionId: ActionId) {
    if (actionId === CLEAR_CONFIGURATIONS_ACTION) {
      return !hasSelectedConfiguration('anchor') && !hasSelectedConfiguration('report');
    }

    if (!isSingleSelection.value) return isBulkActionHidden(actionId);

    const selectedId = selectedField.value?.id;
    return isSingleActionHidden(actionId, selectedId);
  }

  function handleFieldConfiguration(configuration: 'anchor' | 'report') {
    if (formRulesStore.hasSelection) {
      formRulesStore.configureSelectedFields(configuration);
    }
  }

  const availableActions = computed(() => {
    const baseActions = [
      { id: ADD_ANCHOR_ACTION, label: 'Adicionar âncora', icon: MapIcon },
      { id: CLEAR_ANCHOR_ACTION, label: 'Limpar âncora', icon: MapIcon },
      {
        id: REPORT_FIELD_ACTION,
        label: 'Campo para Relatório',
        icon: ChartIcon,
      },
      { id: CLEAR_REPORT_FIELD_ACTION, label: 'Limpar Campo para Relatório', icon: ChartIcon },
      {
        id: CLEAR_CONFIGURATIONS_ACTION,
        label: 'Limpar todas as configurações',
        icon: EraserIcon,
      },
    ];

    const allActions = props.showAddRule
      ? [
      { id: 'add-rule' as const, label: 'Adicionar Regra', icon: ArrowRightCircleIcon },
      ...baseActions,
        ]
      : baseActions;

    return allActions;
  });

  const visibleActions = computed(() =>
    availableActions.value.filter((action) => !isActionHidden(action.id))
  );

  // Closing the menu clears the selection before the menu leaves the screen; with no fields
  // selected, every configuration action would reappear for that last frame.
  const visibleWhileSelected = ref<Set<ActionId> | null>(null);
  watch(
    visibleActions,
    (list) => {
      if (selectedFields.value.length > 0) {
        visibleWhileSelected.value = new Set(list.map((action) => action.id));
      }
    },
    { immediate: true }
  );

  const actions = computed(() => {
    const lastVisible = visibleWhileSelected.value;
    if (selectedFields.value.length > 0 || !lastVisible) return visibleActions.value;
    return availableActions.value.filter((action) => lastVisible.has(action.id));
  });

  function handleActionClick(actionId: ActionId) {
    switch (actionId) {
      case 'add-rule':
        if (formRulesStore.startRuleCreation([...formBuilderStore.elements])) {
          return;
        }

        toast.showWarningToast({
          title: 'Adicione outro elemento para criar uma condição.',
          type: 'warning',
        });
        return;
      case ADD_ANCHOR_ACTION:
        handleFieldConfiguration('anchor');
        return;
      case CLEAR_ANCHOR_ACTION:
        handleClearConfiguration(
          'anchor',
          'Âncora limpa com sucesso',
          'Erro ao limpar âncora. Tente novamente'
        );
        return;
      case REPORT_FIELD_ACTION:
        handleFieldConfiguration('report');
        return;
      case CLEAR_REPORT_FIELD_ACTION:
        handleClearConfiguration(
          'report',
          'Campo para Relatório limpo com sucesso',
          'Erro ao limpar Campo para Relatório. Tente novamente'
        );
        return;
      case CLEAR_CONFIGURATIONS_ACTION:
        handleClearConfiguration(
          'all',
          'Todas as configurações foram limpas',
          'Erro ao limpar configurações. Tente novamente'
        );
        return;
    }
  }

  function handleClearConfiguration(
    configuration: 'anchor' | 'report' | 'all',
    successTitle: string,
    errorTitle: string
  ) {
    try {
      formRulesStore.clearSelectedFields(configuration);
      toast.showSuccessToast({ title: successTitle, type: 'success' });
    } catch {
      toast.showErrorToast({ title: errorTitle, type: 'error' });
    }
  }
</script>

<template>
  <div class="rules-selection-menu" @click.stop>
    <template v-for="action in actions" :key="action.id">
      <button
        type="button"
        class="rules-selection-menu__item"
        :aria-label="action.label"
        @click="handleActionClick(action.id)"
      >
        <IconImage :src="action.icon" :alt="action.label" />
        <span>{{ action.label }}</span>
      </button>
    </template>
  </div>
</template>

<style scoped>
  .rules-selection-menu {
    min-width: 220px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 6px;
    background-color: var(--color-white-01);
    border-radius: 12px;
  }

  .rules-selection-menu__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 36px;
    padding: 8px 10px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--color-gray-07);
    font-size: 14px;
    line-height: 20px;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease;
  }

  .rules-selection-menu__item:hover {
    background-color: var(--color-gray-01);
  }
</style>
